// src/components/suppliers/SupplierOffers.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Tag, 
  Building2, 
  ExternalLink, 
  Calendar, 
  Copy, 
  Check, 
  Search, 
  Award, 
  Loader2 
} from 'lucide-react';

const API_BASE_URL = "https://api.lbnhyderabad.com";

export default function SupplierOffers() {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/offers/public`)
      .then(res => res.json())
      .then(json => {
        if (json.success) setOffers(json.data);
      })
      .catch(err => console.error('Failed to load active offers:', err))
      .finally(() => setLoading(false));
  }, []);

  // Tracking Helper: Fired only when an action takes place
  const trackAction = (offer, actionType) => {
    if (!refCode) return;

    fetch(`${API_BASE_URL}/api/offers/track-interaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refCode,
        offerId: offer.id,
        supplierId: offer.supplier_id,
        actionType // 'promo_copied' OR 'website_opened'
      })
    }).catch(err => console.error('Interaction logging failed:', err));
  };

  // 1. Triggered on Copy Code
  const handleCopyCode = (offer, code) => {
    navigator.clipboard.writeText(code);
    setCopiedId(offer.id);
    setTimeout(() => setCopiedId(null), 2000);
    trackAction(offer, 'promo_copied');
  };

  // 2. Triggered on Website / Offer Link Click
  const handleWebsiteOpen = (offer) => {
    trackAction(offer, 'website_opened');
  };

  const filtered = offers.filter(o =>
    o.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.core_offering_category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.discount_tag?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '36px 16px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
      <div style={{ maxWidth: 1060, margin: '0 auto' }}>
        {/* Header Hero Banner */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fdfaf0', border: '1px solid #febf2c', padding: '4px 12px', borderRadius: 9999, fontSize: '0.72rem', fontWeight: 800, color: '#003666', textTransform: 'uppercase', marginBottom: 10 }}>
            <Award size={14} color="#d97706" />
            <span>Accredited Partner Privileges</span>
          </div>

          <h1 style={{ fontFamily: 'Playfair Display, Georgia, serif', color: '#003666', fontSize: '2rem', margin: '0 0 8px' }}>
            Privilege Supplier Offers Catalog
          </h1>

          <p style={{ color: '#64748b', fontSize: '0.88rem', maxWidth: 580, margin: '0 auto' }}>
            Exclusive commercial discounts & verified corporate benefits unlocked for privilege cardholder {refCode ? <strong style={{ color: '#00529b', fontFamily: 'monospace' }}>({refCode})</strong> : ''}
          </p>

          {/* Search Filter Bar */}
          <div style={{ marginTop: 22, display: 'flex', justifyContent: 'center' }}>
            <div style={{ position: 'relative', maxWidth: 440, width: '100%' }}>
              <Search size={16} color="#94a3b8" style={{ position: 'absolute', top: 11, left: 14 }} />
              <input
                type="text"
                placeholder="Search deals, company, category, or discount..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '10px 14px 10px 38px', borderRadius: 10, border: '1.5px solid #cbd5e1', fontSize: '0.84rem', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
        </div>

        {/* Offers Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#00529b' }}>
            <Loader2 size={36} className="spin-loader" style={{ margin: '0 auto 12px' }} />
            <p style={{ fontSize: '0.86rem' }}>Retrieving live partner offers...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', background: '#fff', border: '1.5px dashed #cbd5e1', borderRadius: 16 }}>
            <Tag size={40} color="#cbd5e1" style={{ margin: '0 auto 12px' }} />
            <h3 style={{ margin: '0 0 6px', color: '#003666' }}>No Active Offers Found</h3>
            <p style={{ fontSize: '0.8rem', color: '#64748b' }}>Check back soon as suppliers update their catalogs daily.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(310px, 1fr))', gap: 20 }}>
            {filtered.map(offer => {
              const primaryImage = offer.images && offer.images.length > 0 
                ? `${API_BASE_URL}${offer.images[0]}` 
                : null;

              return (
                <div
                  key={offer.id}
                  style={{
                    background: '#ffffff',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 16,
                    overflow: 'hidden',
                    boxShadow: '0 4px 16px -4px rgba(0, 50, 100, 0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  {/* Top Image & Badge */}
                  <div style={{ height: 160, background: '#002244', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {primaryImage ? (
                      <img src={primaryImage} alt={offer.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Building2 size={44} color="#febf2c" />
                    )}
                    <div style={{ position: 'absolute', top: 12, left: 12, background: '#febf2c', color: '#002244', fontSize: '0.74rem', fontWeight: 900, padding: '4px 10px', borderRadius: 6 }}>
                      {offer.discount_tag}
                    </div>
                  </div>

                  {/* Body Content */}
                  <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      {/* Vendor Meta */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#00529b', textTransform: 'uppercase' }}>
                          {offer.company_name}
                        </span>
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>• {offer.core_offering_category}</span>
                      </div>

                      <h3 style={{ margin: '0 0 8px', fontSize: '1.05rem', color: '#0f172a', fontWeight: 800, lineHeight: 1.3 }}>
                        {offer.title}
                      </h3>

                      <p style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.5, marginBottom: 14 }}>
                        {offer.description}
                      </p>

                      {/* Promo Code Pill (Copy triggers 'promo_copied' log) */}
                      {offer.promo_code && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fdfaf0', border: '1px solid #febf2c', padding: '4px 10px', borderRadius: 8, marginBottom: 14 }}>
                          <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Promo:</span>
                          <strong style={{ fontFamily: 'monospace', color: '#003666', fontSize: '0.82rem' }}>{offer.promo_code}</strong>
                          <button
                            onClick={() => handleCopyCode(offer, offer.promo_code)}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#00529b' }}
                            title="Copy Promo Code"
                          >
                            {copiedId === offer.id ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Footer Info (Link click triggers 'website_opened' log) */}
                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: '#15803d', fontWeight: 600 }}>
                        <Calendar size={12} />
                        <span>Valid till {offer.valid_until ? offer.valid_until.split('T')[0] : 'Open'}</span>
                      </div>

                      {offer.offer_url ? (
                        <a
                          href={offer.offer_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => handleWebsiteOpen(offer)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#00529b', color: '#febf2c', padding: '6px 12px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                        >
                          <span>Redeem Deal</span>
                          <ExternalLink size={12} />
                        </a>
                      ) : (
                        <span style={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic' }}>
                          Show Privilege ID in-store
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}